import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    full_name: '',
    phone: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    full_name: '',
    phone: '',
  });
  const [touched, setTouched] = useState({
    username: false,
    email: false,
    password: false,
    password2: false,
    full_name: false,
    phone: false,
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);

  const navigate = useNavigate();
  const { register } = useAuth();

  const validateField = (name, value, allFormData = formData) => {
    let error = '';

    switch (name) {
      case 'full_name':
        if (!value.trim()) {
          error = 'Full name is required';
        } else if (value.trim().length < 2) {
          error = 'Full name must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          error = 'Full name can only contain letters and spaces';
        }
        break;
      case 'username':
        if (!value.trim()) {
          error = 'Username is required';
        } else if (value.length < 3) {
          error = 'Username must be at least 3 characters';
        } else if (!/^[a-zA-Z0-9_]+$/.test(value)) {
          error = 'Username can only contain letters, numbers, and underscores';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!value.trim()) {
          error = 'Phone number is required';
        } else if (!/^[0-9]{10}$/.test(value.replace(/[\s-]/g, ''))) {
          error = 'Please enter a valid 10-digit phone number';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 8) {
          error = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])/.test(value)) {
          error = 'Password must contain at least one lowercase letter';
        } else if (!/(?=.*[A-Z])/.test(value)) {
          error = 'Password must contain at least one uppercase letter';
        } else if (!/(?=.*\d)/.test(value)) {
          error = 'Password must contain at least one number';
        }
        break;
      case 'password2':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== allFormData.password) {
          error = 'Passwords do not match';
        }
        break;
      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value,
    };
    setFormData(newFormData);

    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value, newFormData);
      setFieldErrors({
        ...fieldErrors,
        [name]: error,
      });
    }

    // If password changes, revalidate password2 if it's been touched
    if (name === 'password' && touched.password2) {
      const password2Error = validateField('password2', formData.password2, newFormData);
      setFieldErrors({
        ...fieldErrors,
        password2: password2Error,
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });

    const error = validateField(name, value);
    setFieldErrors({
      ...fieldErrors,
      [name]: error,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Mark all fields as touched
    setTouched({
      username: true,
      email: true,
      password: true,
      password2: true,
      full_name: true,
      phone: true,
    });

    // Validate all fields
    const errors = {
      full_name: validateField('full_name', formData.full_name),
      username: validateField('username', formData.username),
      email: validateField('email', formData.email),
      phone: validateField('phone', formData.phone),
      password: validateField('password', formData.password),
      password2: validateField('password2', formData.password2),
    };

    setFieldErrors(errors);

    // If there are validation errors, don't submit
    if (Object.values(errors).some(err => err !== '')) {
      return;
    }

    setLoading(true);

    try {
      const response = await register(formData);

      // Redirect based on role
      if (response.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      // Handle backend validation errors
      if (err.response?.data) {
        const backendErrors = err.response.data;

        // Check if there are field-specific errors from backend
        const newFieldErrors = { ...fieldErrors };
        let hasFieldError = false;

        // Handle username duplicate error
        if (backendErrors.username) {
          newFieldErrors.username = Array.isArray(backendErrors.username)
            ? backendErrors.username[0]
            : backendErrors.username;
          hasFieldError = true;
        }

        // Handle email duplicate error
        if (backendErrors.email) {
          newFieldErrors.email = Array.isArray(backendErrors.email)
            ? backendErrors.email[0]
            : backendErrors.email;
          hasFieldError = true;
        }

        // Handle password error
        if (backendErrors.password) {
          newFieldErrors.password = Array.isArray(backendErrors.password)
            ? backendErrors.password[0]
            : backendErrors.password;
          hasFieldError = true;
        }

        // Handle full_name error
        if (backendErrors.full_name) {
          newFieldErrors.full_name = Array.isArray(backendErrors.full_name)
            ? backendErrors.full_name[0]
            : backendErrors.full_name;
          hasFieldError = true;
        }

        // Handle phone error
        if (backendErrors.phone) {
          newFieldErrors.phone = Array.isArray(backendErrors.phone)
            ? backendErrors.phone[0]
            : backendErrors.phone;
          hasFieldError = true;
        }

        if (hasFieldError) {
          setFieldErrors(newFieldErrors);
          // Set a general error message as well
          setError('Please fix the errors below and try again.');
        } else {
          // If no field-specific errors, show general error
          setError(backendErrors.detail || backendErrors.message || 'Registration failed. Please try again.');
        }
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 py-8">
      <div className="w-full max-w-6xl bg-white shadow-[0_2px_10px_-3px_rgba(6,81,237,0.3)] rounded-md overflow-hidden">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Form Section */}
          <div className="p-6 md:p-8 lg:p-12">
            <form className="max-w-md mx-auto w-full" onSubmit={handleSubmit}>
              <div className="mb-8">
                <div className="mb-6">
                  <img src="/Logo_png_file-removebg-preview.png" alt="LearnersEdge Logo" className="h-32 w-auto mx-auto my-0" />
                </div>
                <h2 className="text-slate-900 text-2xl md:text-3xl font-semibold">Create Account</h2>
                <p className="text-slate-600 text-sm mt-2">Join us and start your learning journey today</p>
              </div>

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6">
                  {error}
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Full Name</label>
                  <div className="relative flex items-center">
                    <input
                      name="full_name"
                      type="text"
                      value={formData.full_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.full_name && touched.full_name
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Enter your full name"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                  {fieldErrors.full_name && touched.full_name && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.full_name}</p>
                  )}
                </div>

                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Username</label>
                  <div className="relative flex items-center">
                    <input
                      name="username"
                      type="text"
                      value={formData.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.username && touched.username
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Choose a username"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  </div>
                  {fieldErrors.username && touched.username && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.username}</p>
                  )}
                </div>

                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Email</label>
                  <div className="relative flex items-center">
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.email && touched.email
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Enter email address"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4" viewBox="0 0 682.667 682.667">
                      <defs>
                        <clipPath id="a" clipPathUnits="userSpaceOnUse">
                          <path d="M0 512h512V0H0Z"></path>
                        </clipPath>
                      </defs>
                      <g clipPath="url(#a)" transform="matrix(1.33 0 0 -1.33 0 682.667)">
                        <path fill="none" strokeMiterlimit="10" strokeWidth="40" d="M452 444H60c-22.091 0-40-17.909-40-40v-39.446l212.127-157.782c14.17-10.54 33.576-10.54 47.746 0L492 364.554V404c0 22.091-17.909 40-40 40Z"></path>
                        <path d="M472 274.9V107.999c0-11.027-8.972-20-20-20H60c-11.028 0-20 8.973-20 20V274.9L0 304.652V107.999c0-33.084 26.916-60 60-60h392c33.084 0 60 26.916 60 60v196.653Z"></path>
                      </g>
                    </svg>
                  </div>
                  {fieldErrors.email && touched.email && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>
                  )}
                </div>

                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Phone Number</label>
                  <div className="relative flex items-center">
                    <input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.phone && touched.phone
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Enter 10-digit phone number"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" fill="#bbb" stroke="#bbb" className="w-[18px] h-[18px] absolute right-4" viewBox="0 0 24 24">
                      <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                    </svg>
                  </div>
                  {fieldErrors.phone && touched.phone && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.phone}</p>
                  )}
                </div>

                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Password</label>
                  <div className="relative flex items-center">
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.password && touched.password
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Min 8 chars with uppercase, lowercase & number"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#bbb"
                      stroke="#bbb"
                      className="w-[18px] h-[18px] absolute right-4 cursor-pointer"
                      viewBox="0 0 128 128"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <path d="M64 104C22.127 104 1.367 67.496.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24s62.633 36.504 63.496 38.057a4 4 0 0 1 0 3.887C126.633 67.496 105.873 104 64 104zM8.707 63.994C13.465 71.205 32.146 96 64 96c31.955 0 50.553-24.775 55.293-31.994C114.535 56.795 95.854 32 64 32 32.045 32 13.447 56.775 8.707 63.994zM64 88c-13.234 0-24-10.766-24-24s10.766-24 24-24 24 10.766 24 24-10.766 24-24 24zm0-40c-8.822 0-16 7.178-16 16s7.178 16 16 16 16-7.178 16-16-7.178-16-16-16z" />
                    </svg>
                  </div>
                  {fieldErrors.password && touched.password && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>
                  )}
                </div>

                <div>
                  <label className="text-slate-900 text-sm font-medium mb-2 block">Confirm Password</label>
                  <div className="relative flex items-center">
                    <input
                      name="password2"
                      type={showPassword2 ? "text" : "password"}
                      value={formData.password2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full text-sm text-slate-900 bg-slate-100 focus:bg-transparent pl-4 pr-10 py-3 rounded-md border outline-none transition-all ${
                        fieldErrors.password2 && touched.password2
                          ? 'border-red-500 focus:border-red-500'
                          : 'border-slate-100 focus:border-blue-600'
                      }`}
                      placeholder="Confirm your password"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="#bbb"
                      stroke="#bbb"
                      className="w-[18px] h-[18px] absolute right-4 cursor-pointer"
                      viewBox="0 0 128 128"
                      onClick={() => setShowPassword2(!showPassword2)}
                    >
                      <path d="M64 104C22.127 104 1.367 67.496.504 65.943a4 4 0 0 1 0-3.887C1.367 60.504 22.127 24 64 24s62.633 36.504 63.496 38.057a4 4 0 0 1 0 3.887C126.633 67.496 105.873 104 64 104zM8.707 63.994C13.465 71.205 32.146 96 64 96c31.955 0 50.553-24.775 55.293-31.994C114.535 56.795 95.854 32 64 32 32.045 32 13.447 56.775 8.707 63.994zM64 88c-13.234 0-24-10.766-24-24s10.766-24 24-24 24 10.766 24 24-10.766 24-24 24zm0-40c-8.822 0-16 7.178-16 16s7.178 16 16 16 16-7.178 16-16-7.178-16-16-16z" />
                    </svg>
                  </div>
                  {fieldErrors.password2 && touched.password2 && (
                    <p className="text-red-500 text-xs mt-1">{fieldErrors.password2}</p>
                  )}
                </div>

                <div className="text-sm">
                  <Link to="/" className="text-blue-600 font-medium hover:underline">
                    Back to Home
                  </Link>
                </div>
              </div>

              <div className="mt-10">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full shadow-xl py-3 px-4 text-[15px] tracking-wide font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  {loading ? 'Creating account...' : 'Create Account'}
                </button>
                <p className="text-sm mt-6 text-center text-slate-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-blue-600 font-medium tracking-wide hover:underline ml-1">
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Image/Info Section - Hidden on mobile, visible on md and up */}
          <div className="hidden md:flex relative bg-gradient-to-br from-blue-50 to-blue-100 min-h-[400px] lg:min-h-[600px]">
            <div className="absolute inset-0 bg-blue-600/70"></div>
            <div className="relative z-10 flex items-center justify-center p-8 lg:p-12 w-full">
              <div className="text-center max-w-md">
                <h1 className="text-white text-3xl lg:text-4xl font-semibold">Join Us Today!</h1>
                <p className="text-slate-100 text-sm lg:text-[15px] font-medium mt-6 leading-relaxed">
                  Create your account and unlock access to premium courses. Start learning, growing, and achieving your educational goals with our comprehensive learning platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
